import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Custom debounce hook for input handling
function useDebouncedCallback<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as T,
    [delay]
  );
}

interface QuizQuestion {
  id: string;
  question: string;
  options: { id: string; text: string; isCorrect: boolean }[];
  timeLimit: number;
  points: number;
  imageUrl?: string;
}

interface MobileQuestionEditorProps {
  question: QuizQuestion;
  questionNumber: number;
  onChange: (updates: Partial<QuizQuestion>) => void;
  onBack: () => void;
  onDelete: () => void;
}

const optionColors = [
  { bg: 'bg-red-500', hover: 'hover:bg-red-600', light: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
  { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', light: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  { bg: 'bg-yellow-500', hover: 'hover:bg-yellow-600', light: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-600 dark:text-yellow-400' },
  { bg: 'bg-green-500', hover: 'hover:bg-green-600', light: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', light: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
  { bg: 'bg-pink-500', hover: 'hover:bg-pink-600', light: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-600 dark:text-pink-400' },
];

const timeLimitOptions = [
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 15, label: '15s' },
  { value: 20, label: '20s' },
  { value: 30, label: '30s' },
  { value: 45, label: '45s' },
  { value: 60, label: '1m' },
  { value: 90, label: '1.5m' },
  { value: 120, label: '2m' },
];

const pointsOptions = [
  { value: 500, label: '500' },
  { value: 1000, label: '1000' },
  { value: 1500, label: '1500' },
  { value: 2000, label: '2000' },
];

export default function MobileQuestionEditor({
  question,
  questionNumber,
  onChange,
  onBack,
  onDelete,
}: MobileQuestionEditorProps) {
  const { t } = useTranslation('mobile');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showPointsPicker, setShowPointsPicker] = useState(false);
  const [imageError, setImageError] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevQuestionIdRef = useRef(question.id);

  // Ref to always have latest question.options for debounced callbacks
  const questionOptionsRef = useRef(question.options);
  useEffect(() => {
    questionOptionsRef.current = question.options;
  }, [question.options]);

  // Local state for debounced inputs
  const [localQuestionText, setLocalQuestionText] = useState(question.question);
  const [localImageUrl, setLocalImageUrl] = useState(question.imageUrl || '');
  const [localOptionTexts, setLocalOptionTexts] = useState<Record<string, string>>(() => {
    const texts: Record<string, string> = {};
    question.options.forEach(opt => { texts[opt.id] = opt.text; });
    return texts;
  });

  // Sync local state only when question.id changes (new question selected)
  useEffect(() => {
    if (prevQuestionIdRef.current !== question.id) {
      setImageError(false);
      setLocalQuestionText(question.question);
      setLocalImageUrl(question.imageUrl || '');
      const texts: Record<string, string> = {};
      question.options.forEach(opt => { texts[opt.id] = opt.text; });
      setLocalOptionTexts(texts);
      prevQuestionIdRef.current = question.id;
    }
  }, [question.id]); // Only depend on question.id to avoid unnecessary resets

  // Debounced onChange handlers (300ms delay)
  const debouncedQuestionChange = useDebouncedCallback(
    (text: string) => onChange({ question: text }),
    300
  );

  const debouncedOptionTextChange = useDebouncedCallback(
    (optionId: string, text: string) => {
      // Use ref to get latest options, avoiding stale closure
      onChange({
        options: questionOptionsRef.current.map(opt =>
          opt.id === optionId ? { ...opt, text } : opt
        ),
      });
    },
    300
  );

  const debouncedImageUrlChange = useDebouncedCallback(
    (url: string) => {
      setImageError(false);
      onChange({ imageUrl: url || undefined });
    },
    300
  );

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleImageUrlChange = useCallback((url: string) => {
    setLocalImageUrl(url);
    debouncedImageUrlChange(url);
  }, [debouncedImageUrlChange]);

  const handleQuestionTextChange = useCallback((text: string) => {
    setLocalQuestionText(text);
    debouncedQuestionChange(text);
  }, [debouncedQuestionChange]);

  const handleOptionTextChange = useCallback((optionId: string, text: string) => {
    setLocalOptionTexts(prev => ({ ...prev, [optionId]: text }));
    debouncedOptionTextChange(optionId, text);
  }, [debouncedOptionTextChange]);

  const addOption = useCallback(() => {
    if (question.options.length >= 6) return;
    const newOption = {
      id: crypto.randomUUID(),
      text: '',
      isCorrect: false,
    };
    // Sync new option to local state for consistency
    setLocalOptionTexts(prev => ({ ...prev, [newOption.id]: '' }));
    onChange({ options: [...question.options, newOption] });
  }, [question.options, onChange]);

  const updateOption = useCallback((optionId: string, updates: Partial<{ text: string; isCorrect: boolean }>) => {
    onChange({
      options: question.options.map(opt =>
        opt.id === optionId ? { ...opt, ...updates } : opt
      ),
    });
  }, [question.options, onChange]);

  const removeOption = useCallback((optionId: string) => {
    if (question.options.length <= 2) return;
    // Clean up local state for removed option
    setLocalOptionTexts(prev => {
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
    onChange({ options: question.options.filter(opt => opt.id !== optionId) });
  }, [question.options, onChange]);

  const toggleCorrect = useCallback((optionId: string) => {
    const option = question.options.find(o => o.id === optionId);
    if (!option) return;
    updateOption(optionId, { isCorrect: !option.isCorrect });
  }, [question.options, updateOption]);

  const correctCount = question.options.filter(o => o.isCorrect).length;

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              aria-label="Go back"
              className="p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="font-semibold text-gray-900 dark:text-white">
                {t('questionEditor.question', { number: questionNumber })}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {correctCount === 0 ? t('questionEditor.noCorrectAnswer') : `${correctCount} correct answer${correctCount > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Delete question"
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg touch-manipulation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-32">
        {/* Question Text */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('questionEditor.question', { number: '' }).replace(' ', '')}
          </label>
          <textarea
            ref={textareaRef}
            value={localQuestionText}
            onChange={(e) => handleQuestionTextChange(e.target.value)}
            placeholder={t('questionEditor.questionPlaceholder')}
            className="w-full px-4 py-4 border border-gray-200 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-800 dark:text-white text-lg resize-none"
            rows={3}
          />
        </div>

        {/* Time and Points */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => setShowTimePicker(true)}
            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl touch-manipulation"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('questionEditor.timeLimit')}</p>
                <p className="font-semibold text-gray-900 dark:text-white">{question.timeLimit}s</p>
              </div>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            onClick={() => setShowPointsPicker(true)}
            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl touch-manipulation"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('questionEditor.points')}</p>
                <p className="font-semibold text-gray-900 dark:text-white">{question.points}</p>
              </div>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Answer Options */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('questionEditor.answerOptions')}
            </label>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('questionEditor.markCorrect')}
            </span>
          </div>

          <div className="space-y-3">
            {question.options.map((option, index) => {
              const color = optionColors[index % optionColors.length];
              return (
                <div
                  key={option.id}
                  className="flex items-center gap-3"
                >
                  {/* Color badge */}
                  <div className={`w-12 h-12 ${color.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white font-bold text-lg">
                      {String.fromCharCode(65 + index)}
                    </span>
                  </div>

                  {/* Input */}
                  <input
                    type="text"
                    value={localOptionTexts[option.id] ?? option.text}
                    onChange={(e) => handleOptionTextChange(option.id, e.target.value)}
                    placeholder={t('questionEditor.optionPlaceholder', { number: String.fromCharCode(65 + index) })}
                    className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-800 dark:text-white text-base"
                  />

                  {/* Correct toggle */}
                  <button
                    onClick={() => toggleCorrect(option.id)}
                    aria-label={option.isCorrect ? `Mark option ${String.fromCharCode(65 + index)} as incorrect` : `Mark option ${String.fromCharCode(65 + index)} as correct`}
                    className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors touch-manipulation ${
                      option.isCorrect
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>

                  {/* Remove */}
                  {question.options.length > 2 && (
                    <button
                      onClick={() => removeOption(option.id)}
                      aria-label={`Remove option ${String.fromCharCode(65 + index)}`}
                      className="p-2 text-gray-400 hover:text-red-500 rounded-lg touch-manipulation"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add Option */}
          {question.options.length < 6 && (
            <button
              onClick={addOption}
              className="w-full mt-3 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2 active:bg-gray-50 dark:active:bg-gray-800 transition-colors touch-manipulation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('questionEditor.addOption')} ({question.options.length}/6)
            </button>
          )}
        </div>

        {/* Image URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Image URL (optional)
          </label>
          <input
            type="url"
            value={localImageUrl}
            onChange={(e) => handleImageUrlChange(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-800 dark:text-white text-base"
          />
          {localImageUrl && (
            <div className="mt-3 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
              {imageError ? (
                <div className="py-6 text-center">
                  <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load image</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Check the URL and try again</p>
                </div>
              ) : (
                <img
                  src={localImageUrl}
                  alt="Question preview"
                  className="max-h-48 mx-auto object-contain"
                  onError={handleImageError}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Done Button - Fixed to bottom of viewport */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 safe-area-inset-bottom z-40">
        <button
          onClick={onBack}
          className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold shadow-lg shadow-orange-500/30 active:scale-[0.98] transition-transform touch-manipulation"
        >
          {t('quizSettings.done')}
        </button>
      </div>

      {/* Time Picker Modal */}
      {showTimePicker && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setShowTimePicker(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom">
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            <div className="px-4 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('questionEditor.timeLimit')}</h3>
              <div className="grid grid-cols-3 gap-3">
                {timeLimitOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onChange({ timeLimit: opt.value });
                      setShowTimePicker(false);
                    }}
                    className={`py-4 rounded-xl font-medium transition-colors touch-manipulation ${
                      question.timeLimit === opt.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Points Picker Modal */}
      {showPointsPicker && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setShowPointsPicker(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom">
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            <div className="px-4 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('questionEditor.points')}</h3>
              <div className="grid grid-cols-2 gap-3">
                {pointsOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onChange({ points: opt.value });
                      setShowPointsPicker(false);
                    }}
                    className={`py-4 rounded-xl font-medium transition-colors touch-manipulation ${
                      question.points === opt.value
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {opt.label} pts
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-scaleIn">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('questionEditor.deleteQuestion')}?
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {t('fieldConfigurator.deleteConfirm')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium touch-manipulation"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    onDelete();
                  }}
                  className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium touch-manipulation"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
